import type { EChartsOption } from 'echarts';

/** The dataZoom window, as the percentages ECharts reports and accepts. */
export interface ZoomRange {
  start: number;
  end: number;
}

export const FULL_ZOOM_RANGE: ZoomRange = { start: 0, end: 100 };

type ZoomComponent = { start?: unknown; end?: unknown };

const asArray = (value: unknown): ZoomComponent[] =>
  Array.isArray(value) ? (value as ZoomComponent[]) : value ? [value as ZoomComponent] : [];

/**
 * The window a chart is currently showing, or `undefined` if it has no dataZoom
 * or has not reported one yet. Both dataZoom components (wheel and slider) hold
 * the same range, so the first one answers for all of them.
 */
export const readZoomRange = (option: EChartsOption | undefined): ZoomRange | undefined => {
  const [first] = asArray((option as { dataZoom?: unknown } | undefined)?.dataZoom);

  return typeof first?.start === 'number' && typeof first?.end === 'number'
    ? { start: first.start, end: first.end }
    : undefined;
};

/**
 * The window a `dataZoom` event is reporting.
 *
 * The slider hands its range over directly; wheel and drag zooming inside the
 * plot arrive as a batch of ranges, one per dataZoom component, all identical.
 * Charts that report neither leave the range to be read off the instance.
 */
export const readZoomRangeFromEvent = (params: unknown): ZoomRange | undefined => {
  const event = params as { start?: unknown; end?: unknown; batch?: ZoomComponent[] } | undefined;
  const source = typeof event?.start === 'number' ? event : event?.batch?.[0];

  return typeof source?.start === 'number' && typeof source?.end === 'number'
    ? { start: source.start, end: source.end }
    : undefined;
};

/**
 * Puts the live window back into a freshly built option.
 *
 * Chart options carry `start: 0, end: 100`, and every rebuild — a resize, a
 * relayout of the axis — is merged into the running chart, which would otherwise
 * snap a zoomed-in user back out to the full range.
 */
export const applyZoomRange = (option: EChartsOption, range: ZoomRange): EChartsOption => {
  const zooms = asArray((option as { dataZoom?: unknown }).dataZoom);

  if (!zooms.length) {
    return option;
  }

  return { ...option, dataZoom: zooms.map((zoom) => ({ ...zoom, ...range })) } as EChartsOption;
};
